import { Activity, PieChart } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { computeMetrics, resolveMacros, SEX_OPTIONS, FORMULA_OPTIONS, LAF_OPTIONS, MACRO_REF_OPTIONS } from "@/lib/calc";
import { fmt } from "@/lib/format";

const Metric = ({ label, value, unit, sub, color, testid }) => (
  <div className="panel px-4 py-4" data-testid={testid}>
    <div className="field-label">{label}</div>
    <div className="mt-2 flex items-baseline gap-1.5">
      <span className="metric-num text-3xl" style={{ color }}>{value}</span>
      {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
    </div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const Toggle = ({ options, value, onChange, testid }) => (
  <div className="inline-flex rounded-lg border border-white/10 bg-black/25 p-0.5" data-testid={testid}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${value === v ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
    ))}
  </div>
);

const MacroInput = ({ label, value, onChange, accent }) => (
  <div>
    <div className="field-label mb-1" style={{ color: accent }}>{label}</div>
    <input
      type="number"
      className="lab-input font-mono-num"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`macro-input-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
    />
  </div>
);

export default function MetabolismTab({ patient, update, macro, setMacro }) {
  const m = computeMetrics(patient);

  const isGk = macro.mode === "gkg";
  const sum = (parseFloat(macro.carbP) || 0) + (parseFloat(macro.protP) || 0) + (parseFloat(macro.fatP) || 0);
  const grams = resolveMacros({ ...patient, macro });
  const refW = grams.refWeight;
  const gk = (g) => (refW > 0 ? g / refW : 0);
  const pct = (g, k) => (grams.kcal > 0 ? (g * k * 100) / grams.kcal : 0);
  const set = (patch) => setMacro({ ...macro, ...patch });

  const donut = [
    { key: "C", val: grams.carbG * 4, color: "hsl(var(--c-carb))" },
    { key: "P", val: grams.protG * 4, color: "hsl(var(--c-prot))" },
    { key: "G", val: grams.fatG * 9, color: "hsl(var(--c-fat))" },
  ];
  let acc = 0;
  const totalDeg = donut.reduce((a, d) => a + d.val, 0) || 1;
  const gradient = donut
    .map((d) => {
      const start = (acc / totalDeg) * 360;
      acc += d.val;
      const end = (acc / totalDeg) * 360;
      return `${d.color} ${start}deg ${end}deg`;
    })
    .join(", ");

  const num = (v) => (v === undefined || v === null ? "" : v);

  return (
    <div className="space-y-6" data-testid="tab-metabolism">
      <section className="panel p-5 sm:p-6">
        <h2 className="mb-5 flex items-center gap-2 font-display text-lg font-bold">
          <Activity className="h-5 w-5 text-primary" /> Calcolo Metabolico & Peso Ideale
        </h2>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <div className="field-label mb-1">Sesso</div>
            <Select value={patient.sesso} onValueChange={(v) => update({ sesso: v })}>
              <SelectTrigger className="lab-input h-auto" data-testid="sesso-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SEX_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="field-label mb-1">Età (anni)</div>
            <input type="number" className="lab-input font-mono-num" value={num(patient.eta)} onChange={(e) => update({ eta: e.target.value })} data-testid="eta-input" />
          </div>
          <div>
            <div className="field-label mb-1">Mestiere</div>
            <input type="text" className="lab-input" placeholder="es. Impiegato" value={patient.mestiere || ""} onChange={(e) => update({ mestiere: e.target.value })} data-testid="mestiere-input" />
          </div>
          <div>
            <div className="field-label mb-1">Peso (kg)</div>
            <input type="number" className="lab-input font-mono-num" value={num(patient.peso)} onChange={(e) => update({ peso: e.target.value })} data-testid="peso-input" />
          </div>
          <div>
            <div className="field-label mb-1">Altezza (cm)</div>
            <input type="number" className="lab-input font-mono-num" value={num(patient.altezza)} onChange={(e) => update({ altezza: e.target.value })} data-testid="altezza-input" />
          </div>
          <div>
            <div className="field-label mb-1">Formula BMR</div>
            <Select value={patient.formula} onValueChange={(v) => update({ formula: v })}>
              <SelectTrigger className="lab-input h-auto" data-testid="formula-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORMULA_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <div className="field-label mb-1">Livello di Attività (LAF)</div>
            <Select value={String(patient.laf)} onValueChange={(v) => update({ laf: parseFloat(v) })}>
              <SelectTrigger className="lab-input h-auto" data-testid="laf-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LAF_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric testid="metric-bmr" label="BMR" value={fmt(m.bmr)} unit="kcal/g" sub="Metabolismo basale" color="hsl(var(--c-kcal))" />
          <Metric testid="metric-tdee" label="TDEE" value={fmt(m.tdee)} unit="kcal/g" sub="Fabbisogno giornaliero" color="hsl(var(--c-prot))" />
          <Metric testid="metric-bmi" label="BMI" value={fmt(m.bmi, 1)} unit="kg/m²" sub={m.bmiCat} color="hsl(var(--c-teal))" />
          <Metric testid="metric-peso-ideale" label="Peso Ideale" value={fmt(m.devine, 1)} unit="kg" sub={`Devine · Lorentz ${fmt(m.lorentz, 1)}kg`} color="hsl(var(--c-carb))" />
        </div>

        <div className="mt-3 rounded-lg border border-white/5 bg-black/20 px-4 py-3 text-sm text-muted-foreground">
          Intervallo fisiologico (BMI 18.5–24.9): <span className="font-mono-num text-foreground">{fmt(m.rangeMin, 1)}–{fmt(m.rangeMax, 1)} kg</span>
        </div>
      </section>

      <section className="panel p-5 sm:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <PieChart className="h-5 w-5 text-primary" /> Distribuzione Calorica & Macro
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_auto]">
          <div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <div className="field-label mb-1">Calcola su</div>
                <Toggle options={[["pct", "% calorie (TDEE)"], ["gkg", "g/kg"]]} value={isGk ? "gkg" : "pct"} onChange={(v) => set({ mode: v })} testid="macro-mode" />
              </div>
              <div>
                <div className="field-label mb-1">Peso di riferimento</div>
                <Toggle options={MACRO_REF_OPTIONS.map((o) => [o.value, o.label])} value={macro.ref || "attuale"} onChange={(v) => set({ ref: v })} testid="macro-ref" />
              </div>
              {macro.ref === "custom" && (
                <div className="w-28">
                  <div className="field-label mb-1">Peso (kg)</div>
                  <input type="number" className="lab-input font-mono-num" value={num(macro.refPeso)} onChange={(e) => set({ refPeso: e.target.value })} data-testid="macro-ref-peso-input" />
                </div>
              )}
              <div className="pb-2 text-xs text-muted-foreground" data-testid="macro-ref-weight">Riferimento: <span className="font-mono-num text-foreground">{fmt(refW, 1)} kg</span></div>
            </div>

            {isGk ? (
              <div className="mt-4 grid grid-cols-3 gap-3">
                <MacroInput label="Carboidrati g/kg" value={num(macro.carbGk)} onChange={(v) => set({ carbGk: v })} accent="hsl(var(--c-carb))" />
                <MacroInput label="Proteine g/kg" value={num(macro.protGk)} onChange={(v) => set({ protGk: v })} accent="hsl(var(--c-prot))" />
                <MacroInput label="Grassi g/kg" value={num(macro.fatGk)} onChange={(v) => set({ fatGk: v })} accent="hsl(var(--c-fat))" />
              </div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <MacroInput label="Carboidrati %" value={num(macro.carbP)} onChange={(v) => set({ carbP: v })} accent="hsl(var(--c-carb))" />
                  <MacroInput label="Proteine %" value={num(macro.protP)} onChange={(v) => set({ protP: v })} accent="hsl(var(--c-prot))" />
                  <MacroInput label="Grassi %" value={num(macro.fatP)} onChange={(v) => set({ fatP: v })} accent="hsl(var(--c-fat))" />
                </div>
                <div className={`mt-2 text-xs font-semibold ${sum === 100 ? "text-primary" : "text-[hsl(var(--c-fat))]"}`}>
                  Somma: {fmt(sum)}%
                </div>
              </>
            )}

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Metric testid="macro-carb" label="Carboidrati" value={fmt(grams.carbG)} unit="g" sub={`${fmt(gk(grams.carbG), 1)} g/kg · ${fmt(pct(grams.carbG, 4))}%`} color="hsl(var(--c-carb))" />
              <Metric testid="macro-prot" label="Proteine" value={fmt(grams.protG)} unit="g" sub={`${fmt(gk(grams.protG), 1)} g/kg · ${fmt(pct(grams.protG, 4))}%`} color="hsl(var(--c-prot))" />
              <Metric testid="macro-fat" label="Grassi" value={fmt(grams.fatG)} unit="g" sub={`${fmt(gk(grams.fatG), 1)} g/kg · ${fmt(pct(grams.fatG, 9))}%`} color="hsl(var(--c-fat))" />
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="relative h-44 w-44">
              <div className="h-full w-full rounded-full" style={{ background: `conic-gradient(${gradient})` }} />
              <div className="absolute inset-[22%] flex flex-col items-center justify-center rounded-full bg-[hsl(var(--card))]">
                <span className="metric-num text-xl text-foreground" data-testid="macro-total-kcal">{fmt(grams.kcal)}</span>
                <span className="text-[10px] text-muted-foreground">kcal</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
